import { toast } from "sonner";
import { buildReceiptLines, ReceiptBlock } from "../ClarityCheck";
import { LOGO_URL } from "../KeshaMark";
import { copyText } from "../../lib/copy";

export default function ReceiptStep({ state, onBack, onNewCheck, onClearSaved }) {
  const blocks = buildReceiptLines(state);

  const toPlainText = () => {
    const lines = [];
    lines.push("THE “AM I TRIPPING?” CLARITY CHECK");
    lines.push("A receipt from Kesha Speaks");
    lines.push("");
    blocks.forEach((b) => {
      lines.push(b.title.toUpperCase());
      lines.push("—".repeat(Math.min(b.title.length, 40)));
      b.items.forEach((it) => {
        if (it.label) lines.push(`${it.label}:`);
        lines.push(it.value);
        lines.push("");
      });
    });
    lines.push("Your answers are saved on this device only. Kesha Speaks does not receive or review your entries.");
    return lines.join("\n");
  };

  const copyReceipt = async () => {
    const ok = await copyText(toPlainText());
    if (ok) {
      toast("Receipt copied to clipboard.");
    } else {
      toast("Couldn't copy automatically — try Print / save as PDF instead.");
    }
  };

  const printReceipt = () => {
    // window.print() is blocked inside cross-origin iframes (preview/embeds).
    // Open a self-contained print-ready page in a new tab and print from there.
    const html = buildPrintHtml(blocks);
    const win = window.open("", "_blank");
    if (!win) {
      // Popup blocked — fall back to in-page print (works on deployed standalone)
      try {
        window.print();
      } catch {
        toast("Your browser blocked the print window. Allow pop-ups and try again.");
      }
      return;
    }
    win.document.open();
    win.document.write(html);
    win.document.close();
    // Trigger print after the new document finishes painting
    win.onload = () => {
      try {
        win.focus();
        win.print();
      } catch {
        /* user can still use Cmd/Ctrl+P inside the new tab */
      }
    };
  };

  return (
    <section className="ks-rise">
      <p className="ks-kicker mb-3">Step 8 of 8</p>
      <h1 className="ks-h2 mb-2">Your Clarity Receipt</h1>
      <p className="text-[17px] leading-[1.6] text-[color:var(--ks-ink-soft)] mb-8">
        A clean snapshot of what you already know. Copy it, print it, or close the tab and let it go.
      </p>

      <div className="flex flex-wrap gap-3 mb-10" data-testid="receipt-actions">
        <button className="ks-btn ks-btn-primary" onClick={copyReceipt} data-testid="receipt-copy-btn">
          Copy receipt
        </button>
        <button className="ks-btn ks-btn-ghost" onClick={printReceipt} data-testid="receipt-print-btn">
          Print / save as PDF
        </button>
        <button className="ks-btn ks-btn-ghost" onClick={onNewCheck} data-testid="receipt-new-btn">
          Start a new check
        </button>
        <button className="ks-btn ks-btn-danger" onClick={onClearSaved} data-testid="receipt-clear-btn">
          Clear saved answers
        </button>
      </div>

      {/* On-screen receipt preview */}
      <article className="ks-card p-5 sm:p-8" data-testid="receipt-preview">
        <div className="flex items-center gap-3 mb-6 pb-5 border-b ks-hair">
          <img src={LOGO_URL} alt="Kesha Speaks" className="h-6 w-auto object-contain" />
          <div className="ml-auto">
            <div className="ks-kicker" style={{ color: "var(--ks-fuchsia)" }}>Clarity Receipt</div>
            <div className="text-[13px] text-[color:var(--ks-ink-soft)]">{formatDate(new Date())}</div>
          </div>
        </div>

        {blocks.map((b, i) => (
          <ReceiptBlock key={i} title={b.title} items={b.items} />
        ))}
      </article>

      <p className="mt-8 text-[13px] leading-[1.6] text-[color:var(--ks-ink-mute)]" data-testid="receipt-privacy">
        Your answers are saved on this device only. Kesha Speaks does not receive or review your entries.
      </p>

      <div className="mt-8 print-hide">
        <button className="ks-btn ks-btn-ghost" onClick={onBack} data-testid="receipt-back-btn">
          ← Back to edit
        </button>
      </div>
    </section>
  );
}

function formatDate(d) {
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const hh = d.getHours().toString().padStart(2, "0");
  const mm = d.getMinutes().toString().padStart(2, "0");
  return `${months[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()} · ${hh}:${mm}`;
}

function escapeHtml(str) {
  return String(str)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function buildPrintHtml(blocks) {
  const sections = blocks
    .map((b) => {
      const items = b.items
        .map((it) => {
          const label = it.label
            ? `<dt>${escapeHtml(it.label)}</dt>`
            : "";
          const value = `<dd>${escapeHtml(it.value).replace(/\n/g, "<br/>")}</dd>`;
          return label + value;
        })
        .join("");
      return `<section><h2>${escapeHtml(b.title)}</h2><dl>${items}</dl></section>`;
    })
    .join("");

  const date = escapeHtml(formatDate(new Date()));

  return `<!doctype html>
<html lang="en">
<head>
<meta charset="utf-8" />
<meta name="viewport" content="width=device-width,initial-scale=1" />
<title>Clarity Receipt — Kesha Speaks</title>
<link rel="preconnect" href="https://fonts.googleapis.com" />
<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin />
<link href="https://fonts.googleapis.com/css2?family=Special+Elite&display=swap" rel="stylesheet" />
<style>
  @page { margin: 22mm 18mm; }
  * { box-sizing: border-box; }
  body { margin: 0; background:#fff; color:#0a0a0b; font-family: Georgia, "Times New Roman", serif; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  .wrap { max-width: 720px; margin: 0 auto; padding: 28px 24px 60px; }
  header.r { display:flex; align-items:flex-end; justify-content:space-between; gap:16px; border-bottom: 1px solid #d9d5cd; padding-bottom: 14px; margin-bottom: 24px; }
  .kicker { font-family:"Special Elite", monospace; font-size: 11px; letter-spacing: .24em; text-transform: uppercase; color:#ff003d; margin-bottom: 6px; }
  h1 { font-size: 34px; line-height: 1.05; margin: 0; }
  .meta { font-family:"Special Elite", monospace; font-size: 11px; letter-spacing:.18em; text-transform: uppercase; color:#4a4a4a; }
  section { margin: 22px 0; padding-bottom: 18px; border-bottom: 1px solid #ece8e0; }
  section:last-of-type { border-bottom: none; }
  h2 { font-size: 20px; line-height:1.2; margin: 0 0 10px; }
  dl { margin: 0; }
  dt { font-family:"Special Elite", monospace; font-size: 10px; letter-spacing: .22em; text-transform: uppercase; color:#6f6a63; margin: 10px 0 3px; }
  dd { margin: 0; font-size: 15px; line-height: 1.55; color:#0a0a0b; white-space: pre-wrap; }
  .privacy { margin-top: 28px; border-top: 1px solid #d9d5cd; padding-top: 12px; font-size: 12px; color:#4a4a4a; }
  .toolbar { font-family:"Special Elite", monospace; text-align:center; padding: 12px; background:#f6f3ec; border-bottom:1px solid #d9d5cd; font-size: 12px; letter-spacing:.16em; text-transform: uppercase; color:#0a0a0b; }
  .toolbar button { font-family:"Special Elite", monospace; font-size: 11px; letter-spacing:.18em; text-transform: uppercase; padding: 8px 14px; border-radius: 999px; border: 1px solid #0a0a0b; background:#0a0a0b; color:#fff; cursor:pointer; margin-left: 12px; }
  .toolbar button.ghost { background:#fff; color:#0a0a0b; }
  @media print { .toolbar { display:none; } }
</style>
</head>
<body>
  <div class="toolbar">
    Use your browser's print dialog · save as PDF or print
    <button onclick="window.print()">Print now</button>
    <button class="ghost" onclick="window.close()">Close</button>
  </div>
  <div class="wrap">
    <header class="r">
      <div>
        <div class="kicker">Kesha Speaks</div>
        <h1>The "Am I Tripping?" Clarity Check</h1>
      </div>
      <div class="meta">${date}</div>
    </header>
    ${sections}
    <div class="privacy">Your answers are saved on this device only. Kesha Speaks does not receive or review your entries.</div>
  </div>
</body>
</html>`;
}
